import { useState, useMemo } from 'react'
import { useTasks } from './hooks/useTasks'
import { useMembers } from '../../hooks/useMembers'
import { useAuth } from '../../hooks/useAuth'
import { useQuery } from '@tanstack/react-query'
import { supabase } from '../../lib/supabase'
import { TaskCard } from './components/TaskCard'
import { TaskDetail } from './components/TaskDetail'
import { TaskForm } from './components/TaskForm'
import { SkeletonList } from '../../components/ui/Skeleton'
import { EmptyState }  from '../../components/ui/EmptyState'
import { SearchBar }   from '../../components/ui/SearchBar'
import { cn } from '../../lib/utils'

const FILTERS = [
  { id: 'all',       label: 'All' },
  { id: 'mine',      label: 'Mine' },
  { id: 'others',    label: 'Others' },
  { id: 'completed', label: 'Done' },
]

const useUpcomingEvents = (householdId) =>
  useQuery({
    queryKey: ['events-upcoming', householdId],
    enabled: !!householdId,
    queryFn: async () => {
      const { data } = await supabase
        .from('events')
        .select('id, title')
        .eq('household_id', householdId)
        .gte('start_time', new Date().toISOString())
        .order('start_time')
        .limit(50)
      return data ?? []
    },
  })

export const TasksPage = () => {
  const { user, household, isLimited } = useAuth()
  const { data: tasks = [], isLoading, completeTask, uncompleteTask } = useTasks()
  const { data: members = [] } = useMembers()
  const { data: events = [] }  = useUpcomingEvents(household?.id)

  const [filter,      setFilter]      = useState('all')
  const [search,      setSearch]      = useState('')
  const [activeTask,  setActiveTask]  = useState(null)
  const [editingTask, setEditingTask] = useState(null)

  const memberMap = useMemo(() => {
    const map = {}
    for (const m of members) {
      if (m.profiles) map[m.profiles.id] = m
    }
    return map
  }, [members])

  const filtered = useMemo(() => {
    let list = tasks
    if (filter === 'mine')      list = list.filter((t) => !t.completed_at && t.assigned_to === user?.id)
    if (filter === 'others')    list = list.filter((t) => !t.completed_at && t.assigned_to && t.assigned_to !== user?.id)
    if (filter === 'completed') list = list.filter((t) => !!t.completed_at)
    if (search.trim()) {
      const q = search.toLowerCase()
      list = list.filter((t) =>
        t.title?.toLowerCase().includes(q) || t.description?.toLowerCase().includes(q)
      )
    }
    return list
  }, [tasks, filter, search, user])

  const sorted = useMemo(() => {
    const mine   = filtered.filter((t) => t.assigned_to === user?.id)
    const others = filtered.filter((t) => t.assigned_to !== user?.id)
    return [...mine, ...others]
  }, [filtered, user])

  const handleToggle = async (task) => {
    task.completed_at ? await uncompleteTask(task.id) : await completeTask(task)
  }

  return (
    <div className="px-4 py-5 max-w-2xl mx-auto">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-xl font-bold text-text-primary">Tasks</h1>
          <p className="text-sm text-text-muted mt-0.5">{tasks.filter((t) => !t.completed_at).length} open</p>
        </div>
        {!isLimited && (
          <button
            onClick={() => setEditingTask('new')}
            className="flex items-center gap-1.5 bg-primary text-white text-sm font-medium px-4 py-2 rounded-xl hover:bg-primary/90 transition-colors"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M12 5v14M5 12h14" />
            </svg>
            New Task
          </button>
        )}
      </div>

      {/* Filter tabs */}
      <div className="flex gap-1 mb-4 bg-background rounded-xl p-1">
        {FILTERS.map((f) => (
          <button
            key={f.id}
            onClick={() => setFilter(f.id)}
            className={cn(
              'flex-1 py-1.5 text-sm font-medium rounded-lg transition-colors',
              filter === f.id ? 'bg-surface text-primary shadow-sm' : 'text-text-muted',
            )}
          >
            {f.label}
          </button>
        ))}
      </div>

      <SearchBar value={search} onChange={setSearch} placeholder="Search tasks…" className="mb-4" />

      {isLoading ? (
        <SkeletonList count={5} />
      ) : sorted.length === 0 ? (
        <EmptyState
          type="tasks"
          title={filter === 'completed' ? 'No completed tasks yet' : 'No tasks here'}
          description={filter === 'completed' ? 'Complete a task to see it here.' : 'Tap New Task to add the first item.'}
        />
      ) : (
        <div className="space-y-2">
          {sorted.map((task) => (
            <TaskCard
              key={task.id}
              task={task}
              member={task.assigned_to ? memberMap[task.assigned_to] : null}
              onToggle={handleToggle}
              onTap={setActiveTask}
            />
          ))}
        </div>
      )}

      {activeTask && !editingTask && (
        <TaskDetail
          task={activeTask}
          member={activeTask.assigned_to ? memberMap[activeTask.assigned_to] : null}
          onEdit={(t) => { setEditingTask(t); setActiveTask(null) }}
          onClose={() => setActiveTask(null)}
        />
      )}

      {editingTask && (
        <TaskForm
          task={editingTask === 'new' ? null : editingTask}
          members={members}
          events={events}
          onClose={() => setEditingTask(null)}
        />
      )}
    </div>
  )
}
